import {
  Add,
  Button,
  FormCard,
  FormItem,
  Input,
  Trash,
  theme
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, type FormInstance } from 'antd'
import { type ChangeEvent, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getIsDifferentRule, getRequiredRule } from 'screens/Rostering/utils'
import { styled } from 'styled-components'

type AbsenceTypesFormListProps = {
  filterDeletedFields: (fieldName: number) => boolean
  deleteHandler: (fieldName: number) => void
  form: FormInstance
}

const AbsenceTypesFormList = ({
  filterDeletedFields,
  deleteHandler,
  form
}: AbsenceTypesFormListProps) => {
  let scrollTimeout: NodeJS.Timeout | null = null
  const lastItemRef = useRef<HTMLDivElement | null>(null)
  const { t } = useTranslation()

  const addAndScroll = (add: () => void) => {
    add()
    if (scrollTimeout) clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => {
      lastItemRef.current?.scrollIntoView({
        behavior: 'smooth'
      })
    }, 100)
  }

  const itemTitleGetter = (item: string) => item || t('NEW_TYPE')

  const getIsAbsenceDifferentRule = (fieldName: number) => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = formValues.absenceTypes || []

    return getIsDifferentRule(currentFormValues, fieldName)
  }

  const getIsAbbreviationDifferentRule = (fieldName: number) => {
    const formValues = form.getFieldsValue(true)
    const currentFormValues = formValues.absenceTypes || []

    return getIsDifferentRule(currentFormValues, fieldName, 'abbreviation')
  }

  return (
    <AntdForm.List name="absenceTypes">
      {(fields, { add }) => (
        <FormCard
          title={t('ABSENCE_TYPES')}
          rightChildren={
            <Button variant="text" onClick={() => addAndScroll(add)}>
              <Add />
              {t('ADD_A_NEW_ABSENCE_TYPE')}
            </Button>
          }
        >
          {fields
            .filter((field) => filterDeletedFields(field.name))
            .map((field) => (
              <FormItem
                key={field.key}
                noStyle
                shouldUpdate={(prevState, currentState) => {
                  return (
                    prevState?.absenceTypes[field.name]?.name !==
                    currentState?.absenceTypes[field.name]?.name
                  )
                }}
              >
                {({ getFieldValue }) => (
                  <div
                    ref={field.name === fields.length - 1 ? lastItemRef : null}
                  >
                    <FormCard
                      key={field.name}
                      level={2}
                      title={itemTitleGetter(
                        getFieldValue(['absenceTypes', field.name, 'name'])
                      )}
                      rightChildren={
                        fields?.filter((field) =>
                          filterDeletedFields(field.name)
                        ).length > 1 &&
                        !getFieldValue([
                          'absenceTypes',
                          field.name,
                          'isMandatory'
                        ]) && (
                          <Button
                            variant="text"
                            onClick={() => deleteHandler(field.name)}
                          >
                            <Trash color={theme.color.neutral.color4} />
                            <DeleteButtonText>{t('DELETE')}</DeleteButtonText>
                          </Button>
                        )
                      }
                    >
                      <FormItem
                        name={[field.name, 'name']}
                        key={['absenceTypes', field.key, 'name'].join('-')}
                        label={<Text>{t('ABSENCE_NAME')}</Text>}
                        rules={[
                          getRequiredRule(t('ABSENCE_NAME')),
                          getIsAbsenceDifferentRule(field.name)
                        ]}
                      >
                        <Input />
                      </FormItem>
                      <FormItem
                        name={[field.name, 'abbreviation']}
                        key={['absenceTypes', field.key, 'abbreviation'].join(
                          '-'
                        )}
                        label={<Text>{t('ABBREVIATION')}</Text>}
                        rules={[
                          getRequiredRule(t('ABBREVIATION')),
                          getIsAbbreviationDifferentRule(field.name)
                        ]}
                      >
                        <Input
                          maxLength={2}
                          // eslint-disable-next-line no-return-assign
                          onInput={(e: ChangeEvent<HTMLInputElement>) =>
                            (e.target.value = e.target.value.toUpperCase())
                          }
                        />
                      </FormItem>
                    </FormCard>
                  </div>
                )}
              </FormItem>
            ))}
        </FormCard>
      )}
    </AntdForm.List>
  )
}

const DeleteButtonText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

export default AbsenceTypesFormList
